import uniq from 'lodash/uniq';

import { getPrintableCharactersRegex, getInvalidCharactersRegex } from './constants/regex';
import { JOBCASE_REGEX, VALID_EMAIL_REGEX } from './regex';

/*
 * Taken from criteria in auth service
 * Password must:
 * Be at least 10 characters
 * Be no more than 128 characters
 * Contain one digit
 * Contain one uppercase character
 * Contain one lowercase character
 * Contain one special character
 *
 * Probably best not to touch this because it will significantly
 * affect the signup flow for our users.
 */
export const isValidPassword = password => (
  password.length >= 10
  && password.length <= 128
  && /^.*\d.*$/.test(password)
  && /^.*[A-Z].*$/.test(password)
  && /^.*[a-z].*$/.test(password)
  && /^.*[\W].*$/.test(password)
);

export const isValidPasswordOld = password => (
  password.length >= 8
  && password.length <= 128
);

export const isValidEmail = email => VALID_EMAIL_REGEX.test(email);

export const isValidZipCode = zipCode => zipCode.match('^[0-9]{5}$') != null;

export const isJobcaseEmail = email => JOBCASE_REGEX.test(email);

export const isValidURL = (url) => {
  if (!url) return false;
  // eslint-disable-next-line no-useless-escape
  return (/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/).test(url);
};

// LUT-538: mirrors hiring-event-service HiringEventValidator.isValidHttpUrl. The native URL
// parser silently percent-encodes characters that the backend's java.net.URI parser rejects
// outright (whitespace, control characters, pipes, malformed escapes), so those are rejected
// explicitly to keep frontend acceptance a subset of the backend's. RFC 3986 sub-delims like
// '*' and '$' (Google Analytics '_gl' parameters) are accepted.
// eslint-disable-next-line no-control-regex
const URL_CHARS_REJECTED_BY_BACKEND = /[\s\u0000-\u001f\u007f-\u009f<>"{}|\\^`]/;
const MALFORMED_PERCENT_ESCAPE = /%(?![0-9A-Fa-f]{2})/;
// The URL parser also ignores extra slashes after the scheme ('https:///x' parses with 'x'
// as the host), which the backend rejects, so the authority must start right after '//'.
const HTTP_SCHEME_AND_AUTHORITY = /^https?:\/\/[^/?#]/i;
// java.net.URI allows '[' and ']' only in an IPv6 authority literal and in the query or
// fragment - never in the path.
const IPV6_AUTHORITY = /^https?:\/\/(?:[^/?#@[\]]*@)?\[[0-9a-f:.]+\]/i;

const hasBracketBeforeQuery = (value) => {
  const stopAt = value.search(/[?#]/);
  const beforeQuery = stopAt === -1 ? value : value.slice(0, stopAt);
  return /[[\]]/.test(beforeQuery.replace(IPV6_AUTHORITY, ''));
};

export const isValidHttpUrl = (value) => {
  if (!value || URL_CHARS_REJECTED_BY_BACKEND.test(value) || MALFORMED_PERCENT_ESCAPE.test(value)) {
    return false;
  }
  if (!HTTP_SCHEME_AND_AUTHORITY.test(value) || hasBracketBeforeQuery(value)) {
    return false;
  }
  let url;
  try {
    url = new URL(value);
  } catch (e) {
    return false;
  }
  return (url.protocol === 'http:' || url.protocol === 'https:') && !!url.hostname;
};

export const containsInvalidCharacters = (value, shouldValidateMarkdown = false) => value && !getPrintableCharactersRegex(shouldValidateMarkdown).test(value);

export const getInvalidCharacters = (value, shouldValidateMarkdown = false) => value.match(getInvalidCharactersRegex(shouldValidateMarkdown));

export const getInvalidCharactersErrorMessage = (chars) => {
  const uniqueChars = uniq(chars);
  const noOfChars = uniqueChars.length;
  const charactersString = uniqueChars.join(', ').replace(/, ([^,]*)$/, ' and $1');
  return `${charactersString} ${noOfChars > 1 ? 'are' : 'is an'} invalid character${noOfChars > 1 ? 's' : ''}`;
};
